/**
 * Effective permission resolution — mirrors backend/constants/permissions.js.
 *
 * Resolution order (the same on both sides, so the UI never shows an action the API rejects):
 *   1. role defaults for the user's role (KPO inherits its parent admin's template)
 *   2. the legacy dense `user.permissions` matrix + historical role safety nets
 *   3. `user.permissionOverrides` — explicit, admin-configured, sparse; always wins
 *   4. business invariants (receipts are never editable)
 */
import {
    MODULES,
    ACTIONS,
    NO_EDIT_MODULES,
    getDefaultPermissionsByRole,
    normalizePermissions,
} from '../constants/permissions';
import { INVOICE_RECEIPT_ADMIN_ROLES } from '../config/roles';

const ADMIN_ROLES_FOR_KPO = ['CompanyAdmin', 'Owner', 'SoleDistributorAdmin', 'SubDistributorAdmin'];
const KPO_ROLES = ['AccountsStaff', 'Accountant'];
const FIELD_ROLES = ['ASM', 'Salesman', 'ZSM', 'RSM', 'NSM'];
/** Roles that always resolve to full access; overrides do not apply to them. */
export const FULL_ACCESS_ROLES = ['Owner', 'SaaSAdmin'];

function blankModule() {
    return { view: false, create: false, edit: false, delete: false, approve: false };
}

function resolveBaseRole(user) {
    if (
        KPO_ROLES.includes(user.role)
        && user.parentAdminRole
        && ADMIN_ROLES_FOR_KPO.includes(user.parentAdminRole)
    ) {
        return user.parentAdminRole;
    }
    return user.role;
}

/**
 * Permissions derived from the role, before any admin-configured overrides.
 *
 * The safety nets below exist because the legacy `user.permissions` matrix is dense, so a
 * stored `false` cannot be told apart from a stale default. They only build the base —
 * deliberate admin decisions are applied afterwards and are never reverted here.
 */
export function getBasePermissions(user) {
    if (!user?.role) return getDefaultPermissionsByRole('StoreStaff');

    const role = user.role;
    const baseRole = resolveBaseRole(user);
    const defaults = getDefaultPermissionsByRole(baseRole) || {};
    const hasStored = user.permissions != null && typeof user.permissions === 'object';

    if (!hasStored) {
        const result = { ...defaults };
        if (KPO_ROLES.includes(role)) {
            result.users = blankModule();
            result.invoices = { ...(result.invoices || {}), edit: false, delete: false };
            result.customers = { ...(result.customers || {}), view: true, create: true, edit: true };
            result.drafts = { ...(result.drafts || {}), view: true, edit: true, approve: true };
            return result;
        }
        if (ADMIN_ROLES_FOR_KPO.includes(baseRole) && role === baseRole) {
            result.invoices = { ...(result.invoices || {}), ...(defaults.invoices || {}), edit: true, delete: true };
        }
        if (FIELD_ROLES.includes(baseRole)) {
            result.reports = { ...(result.reports || {}), view: true };
            result.products = { ...(result.products || {}), view: true };
        }
        return result;
    }

    const merged = { ...defaults };
    Object.keys(defaults).forEach((mod) => {
        merged[mod] = {
            ...(defaults[mod] || {}),
            ...((user.permissions && user.permissions[mod]) || {}),
        };
        if (NO_EDIT_MODULES.includes(mod)) {
            merged[mod].edit = false;
        } else if ((mod === 'invoices' || mod === 'customers') && defaults[mod]?.edit) {
            merged[mod].edit = true;
        }
        if (mod === 'invoices' && defaults[mod]?.delete && INVOICE_RECEIPT_ADMIN_ROLES.includes(role)) {
            merged[mod].delete = true;
        }
    });

    if (FIELD_ROLES.includes(baseRole)) {
        if (defaults.reports?.view) {
            merged.reports = { ...(merged.reports || {}), ...(defaults.reports || {}), view: true };
        }
        if (defaults.products?.view) {
            merged.products = { ...(merged.products || {}), view: true };
        }
        if (defaults.customers?.view && baseRole !== 'Salesman') {
            merged.customers = {
                ...(merged.customers || {}),
                ...(defaults.customers || {}),
                view: true,
                create: defaults.customers?.create !== false,
                edit: defaults.customers?.edit !== false,
            };
        }
        if (defaults.tasks?.view) {
            merged.tasks = {
                ...(merged.tasks || {}),
                ...(defaults.tasks || {}),
                view: true,
                create: defaults.tasks?.create === true ? true : !!(merged.tasks?.create),
                edit: defaults.tasks?.edit === true ? true : !!(merged.tasks?.edit),
            };
        }
        // Restore invoice / draft create for field roles (ASM/Salesman) when role defaults allow it.
        if (defaults.invoices?.view) {
            merged.invoices = {
                ...(merged.invoices || {}),
                ...(defaults.invoices || {}),
                view: true,
                create: defaults.invoices?.create === true ? true : !!(merged.invoices?.create),
                edit: defaults.invoices?.edit === true ? true : !!(merged.invoices?.edit),
                delete: defaults.invoices?.delete === true ? true : !!(merged.invoices?.delete),
                approve: defaults.invoices?.approve === true ? true : !!(merged.invoices?.approve),
            };
        }
        if (defaults.drafts?.view) {
            merged.drafts = {
                ...(merged.drafts || {}),
                ...(defaults.drafts || {}),
                view: true,
                create: defaults.drafts?.create === true ? true : !!(merged.drafts?.create),
                edit: defaults.drafts?.edit === true ? true : !!(merged.drafts?.edit),
                delete: defaults.drafts?.delete === true ? true : !!(merged.drafts?.delete),
                approve: defaults.drafts?.approve === true ? true : !!(merged.drafts?.approve),
            };
        }
        if (defaults.paymentsReceipts?.view) {
            merged.paymentsReceipts = {
                ...(merged.paymentsReceipts || {}),
                ...(defaults.paymentsReceipts || {}),
                view: true,
                create: defaults.paymentsReceipts?.create === true ? true : !!(merged.paymentsReceipts?.create),
            };
        }
    }

    if (KPO_ROLES.includes(role)) {
        merged.users = blankModule();
        merged.invoices = { ...(merged.invoices || {}), edit: false, delete: false };
        if (merged.customers) {
            merged.customers = { ...merged.customers, view: true, create: true, edit: true };
        }
        // Stale stored matrices often left draft edit/approve false. KPO matches tenant admins.
        merged.drafts = { ...(merged.drafts || {}), view: true, edit: true, approve: true };
        return merged;
    }

    if (ADMIN_ROLES_FOR_KPO.includes(baseRole) && role === baseRole) {
        merged.invoices = {
            ...(merged.invoices || {}),
            ...(defaults.invoices || {}),
            view: true,
            create: defaults.invoices?.create !== false,
            edit: true,
            delete: true,
            approve: defaults.invoices?.approve !== false,
        };
    }
    return merged;
}

/**
 * Keep only explicit boolean decisions for known modules/actions.
 * Sparse by design: an absent module/action means "follow the role default".
 */
export function normalizePermissionOverrides(input) {
    if (!input || typeof input !== 'object') return {};
    const out = {};
    Object.keys(MODULES).forEach((mod) => {
        const raw = input[mod];
        if (!raw || typeof raw !== 'object') return;
        const entry = {};
        ACTIONS.forEach((action) => {
            if (typeof raw[action] === 'boolean') entry[action] = raw[action];
        });
        if (NO_EDIT_MODULES.includes(mod)) delete entry.edit;
        if (Object.keys(entry).length) out[mod] = entry;
    });
    return out;
}

/** Apply explicit overrides on top of a resolved permission map. Overrides always win. */
export function applyPermissionOverrides(base, overrides) {
    const normalized = normalizePermissionOverrides(overrides);
    if (!Object.keys(normalized).length) return base;
    const out = { ...base };
    Object.keys(normalized).forEach((mod) => {
        out[mod] = { ...blankModule(), ...(out[mod] || {}), ...normalized[mod] };
    });
    return out;
}

export function getEffectivePermissions(user) {
    if (!user?.role) return getDefaultPermissionsByRole('StoreStaff');
    if (FULL_ACCESS_ROLES.includes(user.role)) return getDefaultPermissionsByRole('Owner');

    const resolved = applyPermissionOverrides(getBasePermissions(user), user.permissionOverrides);
    NO_EDIT_MODULES.forEach((mod) => {
        if (resolved[mod]) resolved[mod] = { ...resolved[mod], edit: false };
    });
    return resolved;
}

/** True when an admin explicitly granted this module/action to this specific user. */
export function hasExplicitGrant(user, module, action) {
    if (!user) return false;
    return normalizePermissionOverrides(user.permissionOverrides)?.[module]?.[action] === true;
}

export function canPerform(user, module, action) {
    if (!user?.role) return false;
    if (FULL_ACCESS_ROLES.includes(user.role)) return true;
    try {
        const perms = getEffectivePermissions(user);
        const mod = perms[module];
        return !!(mod && mod[action] === true);
    } catch (err) {
        console.error('canPerform failed', { module, action, role: user?.role }, err);
        return false;
    }
}

export { normalizePermissions };
